import { type PropsWithChildren, useRef, useState } from 'react';
import getConfig from 'next/config';

import type { Filters, MapConfig, SearchConfig, SearchDestination } from '@/interfaces';
import SearchResults from '../searchResults/SearchResults';
import { SearchResultsSearchForm } from '../searchResultsSearchForm/SearchResultsSearchForm';
import Pagination from '../../containers/pagination/Pagination';
import { TopBarDesktop } from '../../containers/topBarDesktop/TopBarDesktop';
import { EditPanelExpandCallback } from '@/components/editPanel/EditPanel';
import SearchResultsSearchFormPreview from '../searchResultsSearchForm/SearchResultsSearchFormPreview';
import { TopBarMobile } from '../../containers/topBarMobile/TopBarMobile';
import { Broadcast } from 'shared/page/Broadcast';
import { InvalidFiltersWarning } from '../../components/invalidFiltersWarning/InvalidFiltersWarning';
import { SearchResultsFilters } from '../searchResultsFilters/SearchResultsFilters';
import { MapImageButton } from '@/components/mapImageButton/MapImageButton';
import { updateSearchResultsPageQueryParams } from '@/utils/url/routerHelpers';
import { trackMapClick } from '@/utils/tracking/finnAnalytics';
import { SelectedFilters } from '../searchResultsFilters/SelectedFilters';
import { scrollToElementById } from '@/utils/scroll';
import { useIsomorphicLayoutEffect } from '@/utils/useIsomorphicLayoutEffect';

const {
    publicRuntimeConfig: { broadcastUrl },
} = getConfig();

export interface SearchResultsLayoutProps {
    title?: string;
    filterErrors: readonly string[];
    filtersOpen: boolean;
    setFiltersOpen: (open: boolean) => void;
    setMapState: (config: MapConfig) => void;
    numberOfHotels: number;
    filters: Filters;
    destination?: SearchDestination;
    searchConfig: SearchConfig;
}

export const SearchResultsLayout = ({
    title,
    filterErrors,
    numberOfHotels,
    setMapState,
    filtersOpen,
    setFiltersOpen,
    filters,
    destination,
    searchConfig,
    children,
}: PropsWithChildren<SearchResultsLayoutProps>) => {
    const onMapButtonClick = () => {
        setMapState({ open: true });
        updateSearchResultsPageQueryParams(true, filters, searchConfig);
        trackMapClick(true);
    };
    const [openEditPanel, setOpenEditPanel] = useState(!filters.checkInDate || !filters.checkOutDate);
    const focusRef = useRef<HTMLInputElement>();
    const focusOnRender = useRef(false);

    const openEditPanelWithFocus = () => {
        if (!openEditPanel) {
            focusOnRender.current = true;
            setOpenEditPanel(true);
        } else {
            focusRef.current.focus({ preventScroll: true });
        }
        scrollToElementById('editPanel');
    };

    useIsomorphicLayoutEffect(() => {
        if (focusOnRender.current) {
            focusRef.current.focus({ preventScroll: true });
            focusOnRender.current = false;
        }
    });

    return (
        <>
            <div className="u-mt40--lt768">
                <Broadcast url={broadcastUrl} troika />
            </div>
            <div>
                <TopBarDesktop destination={destination} numberOfHotels={numberOfHotels} searchConfig={searchConfig} />
                <div className="panel grid u-mt4">
                    <div className="grid__unit u-r-size1of3" data-testid="result-list-sidebar">
                        <MapImageButton
                            latitude={destination?.latitude}
                            longitude={destination?.longitude}
                            onClick={onMapButtonClick}
                            className="result-list-map-button u-hide-lt768"
                        />
                        <TopBarMobile
                            numberOfHotels={numberOfHotels}
                            filtersOpen={filtersOpen}
                            setFiltersOpen={setFiltersOpen}
                            mapOpen={false}
                            setMapState={setMapState}
                            filters={filters}
                            searchConfig={searchConfig}
                        />
                        <SearchResultsFilters filtersOpen={filtersOpen}>{children}</SearchResultsFilters>
                    </div>
                    <div className="grid__unit result-list u-r-size2of3">
                        <EditPanelExpandCallback
                            expanded={openEditPanel}
                            setExpanded={setOpenEditPanel}
                            preview={<SearchResultsSearchFormPreview />}
                            className="u-mv16 u-ml32--gt768">
                            <SearchResultsSearchForm defaultErrorOnMissingDates checkinDateRef={focusRef} />
                        </EditPanelExpandCallback>
                        {title && (
                            <div className="flex u-ml32--gt768">
                                <div className="u-b1-ruler u-ml8 u-mr16" />
                                <h1 className="result-header u-b1 u-mb0">{title}</h1>
                                <div className="u-b1-ruler u-ml16 u-mr8" />
                            </div>
                        )}
                        <SelectedFilters openFilters={() => setFiltersOpen(true)} />
                        {filterErrors.length > 0 && numberOfHotels > 0 && <InvalidFiltersWarning filterErrors={filterErrors} />}
                        <SearchResults className="result-view u-pl32--gt768" openEditPanelWithFocus={openEditPanelWithFocus} />
                        <Pagination />
                    </div>
                </div>
            </div>
        </>
    );
};
